import { useState } from "react";
import type { ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { forgetUserRememberedDevice } from "../../api/deviceKeys.js";
import type { RememberedDevice } from "../../api/deviceKeys.js";
import { revokeUserSession } from "../../api/sessions.js";
import type { DeviceSession } from "../../api/sessions.js";
import { ApiClientError } from "../../api/http.js";
import { userRememberedDevicesKey, userSessionsKey, useUserRememberedDevices, useUserSessions } from "../../data/account.js";
import { Dialog } from "../../ui/Dialog.js";
import { Alert, Button, Skeleton } from "../../ui/primitives.js";
import { useToast } from "../../ui/Toast.js";
import { RememberedDeviceList } from "../settings/RememberedDeviceList.js";
import { SessionList } from "../settings/SessionList.js";

/** Devices another account is signed in on; the admin can sign out single ones. */
export function UserSessionsDialog(props: { readonly userId: string; readonly email: string; readonly onClose: () => void }): ReactNode {
  const sessions = useUserSessions(props.userId, true);
  const remembered = useUserRememberedDevices(props.userId, true);
  const queryClient = useQueryClient();
  const toast = useToast();
  const [revoking, setRevoking] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);

  async function revoke(session: DeviceSession): Promise<void> {
    setRevoking(session.id);
    setError(undefined);
    try {
      await revokeUserSession(props.userId, session.id);
      toast.notify(`${props.email}: ${session.deviceName} abgemeldet.`, { tone: "success" });
    } catch (err: unknown) {
      setError(err instanceof ApiClientError ? err.message : "Abmelden fehlgeschlagen.");
    } finally {
      setRevoking(undefined);
      await queryClient.invalidateQueries({ queryKey: userSessionsKey(props.userId) });
    }
  }

  async function forget(device: RememberedDevice): Promise<void> {
    setRevoking(device.id);
    setError(undefined);
    try {
      await forgetUserRememberedDevice(props.userId, device.id);
      toast.notify(`${props.email}: ${device.deviceName} vergessen.`, { tone: "success" });
    } catch (err: unknown) {
      setError(err instanceof ApiClientError ? err.message : "Vergessen fehlgeschlagen.");
    } finally {
      setRevoking(undefined);
      await queryClient.invalidateQueries({ queryKey: userRememberedDevicesKey(props.userId) });
      await queryClient.invalidateQueries({ queryKey: userSessionsKey(props.userId) });
    }
  }

  return (
    <Dialog open onClose={props.onClose} title="Angemeldete Geräte" description={props.email} variant="center" size="md">
      <div className="flex flex-col gap-4 p-5">
        {sessions.isPending ? <Skeleton className="h-16" /> : null}
        {sessions.error !== null ? <Alert tone="danger">{sessions.error.message}</Alert> : null}
        {sessions.data !== undefined ? (
          <SessionList sessions={sessions.data} revoking={revoking} onRevoke={(s: DeviceSession): void => void revoke(s)} empty="Das Konto ist gerade auf keinem Gerät angemeldet." />
        ) : null}
        {remembered.data !== undefined && remembered.data.length > 0 ? (
          <div className="flex flex-col gap-2">
            <h3 className="text-sm font-medium text-foam">Gemerkte Geräte</h3>
            <RememberedDeviceList devices={remembered.data} forgetting={revoking} onForget={(d: RememberedDevice): void => void forget(d)} empty="" />
            <p className="text-[0.8125rem] text-mist-2">Vergessen meldet das Gerät ab; es braucht danach wieder den zweiten Faktor.</p>
          </div>
        ) : null}
        {remembered.error !== null ? <Alert tone="danger">{remembered.error.message}</Alert> : null}
        {error !== undefined ? <Alert tone="danger">{error}</Alert> : null}
        <p className="text-[0.8125rem] text-mist-2">
          Abmelden beendet die Sitzung sofort. Die Person kann sich danach normal neu anmelden. Für alle Geräte auf einmal gibt es „Überall abmelden“.
        </p>
        <div className="flex justify-end">
          <Button variant="ghost" onClick={props.onClose}>
            Schließen
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
